/**
 * Login / Signup page component
 */
import { BaseElement } from '../base-element';
import { Signal } from 'signal-polyfill';
import { authActions } from '../../state/auth.state';
import { navigate, setReturnUrl } from '../../router';

type AuthMode = 'login' | 'signup';
type ProviderName = 'microsoft' | 'google' | 'apple' | 'facebook';

interface InviteContext {
  trialName: string;
  role: string;
}

export class LoginPage extends BaseElement {
  private mode = new Signal.State<AuthMode>('login');
  private email = new Signal.State('');
  private loading = new Signal.State(false);
  private inviteContext = new Signal.State<InviteContext | null>(null);
  private loadingAction: ProviderName | 'email' | null = null;
  private inviteToken: string | null = null;

  connectedCallback(): void {
    this.parseQueryParams();
    this.watch(this.mode, () => this.updateRender());
    this.watch(this.email, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.inviteContext, () => this.updateRender());
    this.updateRender();
  }

  private parseQueryParams(): void {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const returnUrl = params.get('returnUrl');
    const invite = params.get('invite');

    if (mode === 'signup') {
      this.mode.set('signup');
    }

    if (returnUrl) {
      setReturnUrl(returnUrl);
    }

    if (invite) {
      this.inviteToken = invite;
      this.mode.set('signup');
      this.inviteContext.set(this.parseInviteToken(invite));
    }
  }

  private parseInviteToken(token: string): InviteContext | null {
    try {
      const decoded = atob(token);
      const parsed = JSON.parse(decoded) as Partial<InviteContext>;
      if (parsed.trialName && parsed.role) {
        return { trialName: parsed.trialName, role: parsed.role };
      }
    } catch {
      // Ignore malformed invite tokens
    }

    const [trialName, role] = token.split(':');
    if (trialName && role) {
      return { trialName, role };
    }

    return { trialName: token, role: 'Invitee' };
  }

  private updateRender(): void {
    const mode = this.mode.get();
    const isSignup = mode === 'signup';
    const email = this.email.get();
    const loading = this.loading.get();
    const inviteContext = this.inviteContext.get();
    const actionLabel = isSignup ? 'Sign up with' : 'Continue with';
    const emailActionLabel = isSignup ? 'Sign up with Email' : 'Continue with Email';
    const inviteBanner = inviteContext && isSignup
      ? `
          <div class="mb-6 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-4 text-sm text-primary-700 shadow-xs dark:border-primary-400/30 dark:bg-primary-500/10 dark:text-primary-200" data-testid="invite-context">
            <div class="font-semibold">You're invited to join ${this.esc(inviteContext.trialName)}</div>
            <div class="mt-1 text-xs uppercase tracking-[0.14em] text-primary-600 dark:text-primary-200/80">Role: ${this.esc(inviteContext.role)}</div>
          </div>
        `
      : '';

    this.render(`
      <div class="relative min-h-screen overflow-hidden bg-background-light px-4 py-8 dark:bg-background-dark" data-testid="login-page">
        <div class="pointer-events-none absolute -left-12 top-0 h-56 w-56 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/40"></div>
        <div class="pointer-events-none absolute -right-8 bottom-0 h-64 w-64 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/30"></div>

        <div class="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center justify-center">
          <div class="grid w-full max-w-5xl overflow-hidden rounded-[32px] border border-neutral-200/80 bg-white/95 shadow-[0_24px_80px_rgba(48,52,37,0.14)] backdrop-blur-sm dark:border-white/10 dark:bg-surface-dark/95 lg:grid-cols-[1.05fr_0.95fr]">
            <section class="hidden flex-col justify-between bg-[linear-gradient(160deg,var(--color-primary-950),var(--color-primary-800)_55%,var(--color-neutral-950))] px-8 py-10 text-white lg:flex">
              <div>
                <div class="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/85">Venom Dog Sports</div>
                <div class="mt-8 max-w-md">
                  <img src="/brand/logo-primary-dark.svg" alt="Venom Dog Sports" class="h-11 w-auto" />
                  <h2 class="mt-8 text-4xl font-bold leading-tight text-white">${isSignup ? 'Join the next trial weekend with less paperwork.' : 'Pick up exactly where your last trial weekend left off.'}</h2>
                  <p class="mt-4 text-base leading-7 text-white/75">Secure sign-in for handlers, secretaries, and course directors with the same moss-and-rose system used across registrations, confirmations, and invites.</p>
                </div>
              </div>

              <div class="grid gap-4">
                <div class="rounded-2xl border border-white/10 bg-white/8 px-5 py-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-200">For handlers</p>
                  <p class="mt-2 text-sm leading-6 text-white/80">Review entries, confirmations, and sign-in links without leaving the branded app flow.</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/8 px-5 py-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">For club staff</p>
                  <p class="mt-2 text-sm leading-6 text-white/80">Invite competitors, manage secretary work, and keep communication consistent across every email touchpoint.</p>
                </div>
              </div>
            </section>

            <section class="relative px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">
              <div class="mb-6 rounded-[28px] border border-primary-100 bg-[linear-gradient(180deg,#fff8ef_0%,#ffffff_100%)] p-5 shadow-xs dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(26,29,20,0.9)_0%,rgba(30,26,24,0.98)_100%)] lg:hidden">
                <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
                <div class="mt-4 text-center">
                  <img src="/brand/logo-primary-light.svg" alt="Venom Dog Sports" class="mx-auto h-10 dark:hidden" />
                  <img src="/brand/logo-primary-dark.svg" alt="Venom Dog Sports" class="hidden mx-auto h-10 dark:block" />
                </div>
                <p class="mt-4 text-center text-sm leading-6 text-neutral-600 dark:text-neutral-300">Secure sign-in for handlers, secretaries, and course directors.</p>
              </div>

              <div class="mb-6 hidden text-center lg:block">
                <img src="/brand/logo-primary-light.svg" alt="Venom Dog Sports" class="mx-auto h-10 dark:hidden" />
                <img src="/brand/logo-primary-dark.svg" alt="Venom Dog Sports" class="hidden mx-auto h-10 dark:block" />
              </div>

              <div class="mx-auto max-w-md">
                <div class="mb-6 text-center">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-600 dark:text-secondary-300">Secure sign-in</p>
                  <h1 class="mt-3 text-3xl font-semibold text-neutral-900 dark:text-white" data-testid="auth-heading">
                    ${isSignup ? 'Create your account' : 'Log in to your account'}
                  </h1>
                  <p class="mt-3 text-sm leading-6 text-neutral-600 dark:text-neutral-300">${isSignup ? 'Set up your account once, then move between invites and registrations without losing context.' : 'Return to registrations, invitations, and secretary tools without restarting your workflow.'}</p>
                </div>

                ${inviteBanner}

                <div class="flex flex-col gap-3" data-testid="provider-buttons">
                  ${this.renderProviderButton('microsoft', 'Microsoft', 'bg-[#2F2F2F] text-white shadow-xs', actionLabel)}
                  ${this.renderProviderButton('google', 'Google', 'border border-neutral-300 bg-white text-[#1F1F1F] shadow-xs dark:border-neutral-700', actionLabel)}
                  ${this.renderProviderButton('apple', 'Apple', 'bg-black text-white shadow-xs', actionLabel)}
                  ${this.renderProviderButton('facebook', 'Facebook', 'bg-[#1877F2] text-white shadow-xs', actionLabel)}
                </div>

                <div class="my-6 flex items-center gap-4" data-testid="divider">
                  <div class="flex-1 border-t border-neutral-200 dark:border-neutral-700"></div>
                  <span class="text-xs uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">or</span>
                  <div class="flex-1 border-t border-neutral-200 dark:border-neutral-700"></div>
                </div>

                <div class="rounded-[28px] border border-neutral-200 bg-neutral-50/80 p-4 shadow-xs dark:border-white/10 dark:bg-neutral-950/40">
                  <div class="flex flex-col gap-3">
                    <label class="text-sm font-medium text-neutral-700 dark:text-neutral-300" for="email-input">Email address</label>
                    <input
                      id="email-input"
                      class="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 shadow-xs focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white dark:focus:ring-primary-500/30"
                      type="email"
                      placeholder="you@example.com"
                      value="${this.esc(email)}"
                      ${loading ? 'disabled' : ''}
                      data-testid="email-input"
                    />
                    <button
                      type="button"
                      class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white shadow-xs transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                      data-testid="email-continue-btn"
                      ${loading ? 'disabled' : ''}
                    >
                      ${this.loadingAction === 'email' && loading ? this.renderSpinner() : ''}
                      <span>${emailActionLabel}</span>
                    </button>
                  </div>
                </div>

                <div class="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400" data-testid="mode-toggle">
                  ${isSignup ? 'Already have an account?' : "Don't have an account?"}
                  <button type="button" class="ml-1 font-medium text-primary-600 hover:underline dark:text-primary-300" data-testid="mode-toggle-link">
                    ${isSignup ? 'Log in' : 'Sign up'}
                  </button>
                </div>

                <p class="mt-6 text-center text-xs leading-6 text-neutral-500 dark:text-neutral-400" data-testid="legal-footer">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    `);

    this.attachEventListeners();
  }

  private renderProviderButton(
    provider: ProviderName,
    label: string,
    classes: string,
    actionLabel: string
  ): string {
    const loading = this.loading.get();
    const isLoading = loading && this.loadingAction === provider;
    const icon = isLoading ? this.renderSpinner() : this.renderProviderIcon(provider);

    return `
      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-xl px-4 py-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${classes}"
        data-testid="provider-btn-${provider}"
        data-provider="${provider}"
        ${loading ? 'disabled' : ''}
      >
        <span class="flex h-5 w-5 items-center justify-center">${icon}</span>
        <span>${actionLabel} ${label}</span>
      </button>
    `;
  }

  private renderProviderIcon(provider: ProviderName): string {
    switch (provider) {
      case 'microsoft':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M3 3h8v8H3z" fill="currentColor"/>
            <path d="M13 3h8v8h-8z" fill="currentColor"/>
            <path d="M3 13h8v8H3z" fill="currentColor"/>
            <path d="M13 13h8v8h-8z" fill="currentColor"/>
          </svg>
        `;
      case 'google':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M12 4a8 8 0 015.657 2.343l-2.1 2.1A5 5 0 0012 7a5 5 0 000 10 4.5 4.5 0 004.314-3H12v-3h7a8 8 0 01-7 9 8 8 0 110-16z" fill="currentColor"/>
          </svg>
        `;
      case 'apple':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M16.5 7.2c-1.1 0-2.3.7-3 1.8-.6 1-.9 2.2-.8 3.4.1 2.3 1.6 4.7 4.1 4.7 1.2 0 1.7-.5 3-.5 1.3 0 1.7.5 3 .5 1.3 0 2.2-1.1 2.8-2.1-.1-.1-2.5-1-2.5-3.9 0-2.5 2-3.6 2.1-3.7-1.2-1.7-3-1.9-3.6-1.9-1.4-.1-2.6.7-3.3.7-.7 0-1.7-.7-2.8-.7z" fill="currentColor"/>
            <path d="M15.6 2.5c-.5.6-.9 1.5-.8 2.4 1 .1 2-.5 2.6-1.2.5-.6.9-1.5.8-2.4-1-.1-2 .5-2.6 1.2z" fill="currentColor"/>
          </svg>
        `;
      case 'facebook':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M14.5 8.5V7c0-1.1.7-1.3 1.2-1.3h1.8V2.1L15 2c-2.9 0-3.6 2.2-3.6 3.6v2H9v3.2h2.4V22h3.1v-11h2.6l.4-3.2h-3z" fill="currentColor"/>
          </svg>
        `;
    }
  }

  private renderSpinner(): string {
    return `<dt-spinner size="small"></dt-spinner>`;
  }

  private attachEventListeners(): void {
    const providerButtons = this.$$('[data-provider]');
    providerButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = event.currentTarget as HTMLElement;
        const provider = target.getAttribute('data-provider') as ProviderName | null;
        if (provider) {
          this.loadingAction = provider;
          this.loading.set(true);
          authActions.loginWithProvider(provider, this.mode.get());
        }
      });
    });

    const emailInput = this.$('[data-testid="email-input"]') as HTMLInputElement | null;
    if (emailInput) {
      emailInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.email.set(target.value);
      });
    }

    const emailButton = this.$('[data-testid="email-continue-btn"]');
    if (emailButton) {
      emailButton.addEventListener('click', () => {
        this.loadingAction = 'email';
        this.loading.set(true);
        authActions.loginWithEmail(this.email.get(), this.mode.get());
      });
    }

    const modeToggle = this.$('[data-testid="mode-toggle-link"]');
    if (modeToggle) {
      modeToggle.addEventListener('click', () => this.handleModeToggle());
    }
  }

  private handleModeToggle(): void {
    const nextMode: AuthMode = this.mode.get() === 'login' ? 'signup' : 'login';
    this.mode.set(nextMode);

    const params = new URLSearchParams(window.location.search);
    if (nextMode === 'signup') {
      params.set('mode', 'signup');
    } else {
      params.delete('mode');
    }

    const returnUrl = sessionStorage.getItem('dt-return-url');
    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    if (this.inviteToken) {
      params.set('invite', this.inviteToken);
    }

    const query = params.toString();
    navigate(`/login${query ? `?${query}` : ''}`, { replace: true });
  }
}

customElements.define('dt-login-page', LoginPage);
